// Admin → Plans. The subscription catalog: one row per plan, with a full
// editor for every field the API accepts and the multi-currency price grid.
//
// Staff tool — English chrome, LTR. `nameKey`/`taglineKey` are translation
// keys shown raw; their resolved AR/EN values are edited in Translations.
//
// ─────────────────────────────────────────────────────────────────────────────
// 🔴 WHAT CHANGED AND WHY. `PATCH /admin/plans/:id` has always accepted every
// plan field, but this page only ever surfaced three of them (highlight,
// status, one price). Editing a plan's limits, features, tier or trial meant
// a hand-written SQL UPDATE against prod. Owner asked for plans to be "fully
// customized"; this is that editor.
//
// 🔴 THE LIMITS ARE THE DANGEROUS PART. `max_kids` / `max_teachers` /
// `max_students` are nullable, and **null means UNLIMITED** — a semantic the
// old row rendered as nothing at all (`maxKids != null && ...`), so an
// unlimited plan and a plan whose limit nobody had set looked identical. The
// editor models a limit as an explicit MODE (Unlimited | Limited to N), never
// as "an empty box means no cap": an empty box is also what you get halfway
// through typing, and `Number('')` is 0 — which the API rejects outright,
// because a plan capped at zero kids would be unusable while looking valid.
// All of that logic is in `-plan-editor.ts` and unit-tested there.
//
// 🔴 SAVE SENDS A DIFF, not the whole plan. The API reads an absent key as
// "leave unchanged", so a full-body PATCH would rewrite every column on every
// save — including ones a colleague changed in another tab since this page
// loaded. `buildPatch` emits only what actually differs.
//
// 🔴 Prices are NOT part of the plan form. `PUT /:id/prices` is a separate
// endpoint keyed by (currency, interval), and each cell saves on its own —
// folding them into the plan Save would turn 14 independent writes into one
// all-or-nothing submit. The grid below is unchanged in behaviour; it only
// gained per-currency precision and an is-dirty affordance.
//
// 🔴 Money: every amount goes through lib/money.ts. `parseMinorAmount` knows
// KWD/BHD/OMR are 3-decimal; the old inline `10 ** digits` here was a second
// copy of that rule waiting to drift. Never sum across currencies.
// ─────────────────────────────────────────────────────────────────────────────

import { Button, Input } from '@fajrarabi/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import {
  LIMIT_LABEL,
  buildCreateInput,
  buildPatch,
  cleanFeatures,
  draftFromPlan,
  emptyDraft,
  isUnchanged,
  limitOf,
  limitSummary,
  setLimitMode,
  trialSummary,
  validate,
  visibleLimits,
  type LimitField,
  type PlanDraft,
  type PlanField,
} from './-plan-editor';

import {
  ConfirmDialog,
  DataTable,
  Drawer,
  FilterBar,
  Icon,
  PageHeader,
  StatusBadge,
  actionsColumn,
  useTableState,
  useToast,
  type Column,
} from '~/components';
import { ApiError } from '~/lib/api';
import {
  archivePlan,
  createPlan,
  listPlans,
  updatePlan,
  upsertPrice,
  type AdminPlan,
} from '~/lib/billing';
import { CURRENCIES, currencyDigits, formatMinorAmount, parseMinorAmount } from '~/lib/money';

export const Route = createFileRoute('/_authed/plans')({
  component: PlansPage,
});

const INTERVALS = ['month', 'year'] as const;

function PlansPage() {
  const qc = useQueryClient();
  const table = useTableState({ filterKeys: ['audience', 'status'] });
  const plansQuery = useQuery({ queryKey: ['plans'], queryFn: ({ signal }) => listPlans(signal) });
  const { toast } = useToast();

  // `null` = closed. `{id}` = editing that plan. `{new:true}` = the create
  // form. One piece of state rather than two booleans, so "creating" and
  // "editing" are structurally unable to be true at once — and a discriminated
  // object rather than `string | 'new'`, which collapses to plain `string` and
  // would let a plan whose id happened to be "new" open the wrong form.
  const [editing, setEditing] = useState<{ id: string } | { new: true } | null>(null);
  const [pricesFor, setPricesFor] = useState<string | null>(null);
  const [pendingArchive, setPendingArchive] = useState<AdminPlan | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['plans'] });

  const setStatus = useMutation({
    mutationFn: ({ plan, status }: { plan: AdminPlan; status: 'active' | 'archived' }) =>
      status === 'archived' ? archivePlan(plan.id) : updatePlan(plan.id, { status: 'active' }),
    onSuccess: invalidate,
  });

  const toggleHighlight = useMutation({
    mutationFn: (plan: AdminPlan) => updatePlan(plan.id, { isHighlighted: !plan.isHighlighted }),
    onSuccess: invalidate,
  });

  const all = useMemo(() => plansQuery.data ?? [], [plansQuery.data]);

  const rows = useMemo(() => {
    let list = all;
    const audience = table.filters.audience;
    if (audience) list = list.filter((p) => p.audience === audience);
    const status = table.filters.status;
    if (status) list = list.filter((p) => p.status === status);
    const q = table.q.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (p) =>
        p.slug.toLowerCase().includes(q) ||
        p.tier.toLowerCase().includes(q) ||
        p.nameKey.toLowerCase().includes(q),
    );
  }, [all, table.q, table.filters.audience, table.filters.status]);

  const editingPlan = editing && 'id' in editing ? all.find((p) => p.id === editing.id) : undefined;
  const pricePlan = pricesFor ? all.find((p) => p.id === pricesFor) : undefined;

  const columns: Column<AdminPlan>[] = [
    {
      key: 'plan',
      header: 'Plan',
      alwaysVisible: true,
      render: (p) => (
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="truncate font-medium text-admin-900">{p.slug}</span>
            {p.isHighlighted && <StatusBadge status="popular" label="Popular" tone="info" />}
          </div>
          <p className="truncate font-mono text-xs text-admin-400">{p.nameKey}</p>
        </div>
      ),
    },
    {
      key: 'audience',
      header: 'Audience',
      hideBelow: 'md',
      render: (p) => <span className="capitalize text-admin-700">{p.audience}</span>,
    },
    {
      key: 'tier',
      header: 'Tier',
      hideBelow: 'lg',
      render: (p) => <span className="text-admin-600">{p.tier}</span>,
    },
    {
      key: 'limits',
      header: 'Limits',
      hideBelow: 'md',
      // 🔴 "Unlimited" is spelled out. The old row printed nothing for a null
      // limit, so an uncapped plan was indistinguishable from an unset one.
      render: (p) => <span className="text-admin-600">{limitSummary(p)}</span>,
    },
    {
      key: 'trial',
      header: 'Trial',
      hideBelow: 'lg',
      render: (p) =>
        p.trialDays > 0 ? (
          <StatusBadge status="trial" label={trialSummary(p)} tone="info" />
        ) : (
          <span className="text-admin-300">None</span>
        ),
    },
    {
      key: 'prices',
      header: 'Prices',
      align: 'end',
      hideBelow: 'md',
      render: (p) => (
        <span className={p.prices.length === 0 ? 'text-warning-600' : 'text-admin-600'}>
          {p.prices.length === 0 ? 'None set' : `${p.prices.length} set`}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      align: 'end',
      render: (p) => <StatusBadge status={p.status} dot />,
    },
    actionsColumn<AdminPlan>({
      label: (p) => `plan ${p.slug}`,
      primary: (p) => ({
        id: 'edit',
        label: 'Edit plan',
        icon: 'edit',
        onSelect: () => setEditing({ id: p.id }),
      }),
      actions: (p) => [
        { id: 'prices', label: 'Edit prices', icon: 'money', onSelect: () => setPricesFor(p.id) },
        {
          id: 'highlight',
          label: p.isHighlighted ? 'Remove “Popular” badge' : 'Mark as “Popular”',
          icon: 'sparkle',
          onSelect: () =>
            toggleHighlight.mutate(p, {
              onSuccess: () =>
                toast(p.isHighlighted ? `${p.slug} unfeatured` : `${p.slug} featured`, {
                  tone: 'success',
                }),
            }),
        },
        {
          id: 'copy-id',
          label: 'Copy id',
          icon: 'copy',
          onSelect: () => void navigator.clipboard?.writeText(p.id),
        },
        // Archive is reversible and never destroys a subscription:
        // `subscriptions.plan_id` is ON DELETE RESTRICT, so an archived plan
        // keeps serving everyone already on it and merely stops being sold.
        p.status === 'archived'
          ? {
              id: 'restore',
              label: 'Restore plan',
              icon: 'undo' as const,
              onSelect: () =>
                setStatus.mutate(
                  { plan: p, status: 'active' },
                  { onSuccess: () => toast(`Plan ${p.slug} restored`, { tone: 'success' }) },
                ),
            }
          : {
              id: 'archive',
              label: 'Archive plan',
              icon: 'inbox' as const,
              onSelect: () => setPendingArchive(p),
            },
      ],
    }),
  ];

  return (
    <div className="mx-auto max-w-7xl">
      <PageHeader
        title="Plans"
        description="The subscription catalog. Every plan field is editable here; prices are per currency and interval."
        actions={
          <Button onClick={() => setEditing({ new: true })}>
            <Icon name="plus" className="h-4 w-4" />
            New plan
          </Button>
        }
      />

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(p) => p.id}
        loading={plansQuery.isLoading}
        error={plansQuery.error}
        onRetry={() => void plansQuery.refetch()}
        onRowClick={(p) => setEditing({ id: p.id })}
        columnVisibility
        caption="Plans"
        skeletonRows={4}
        empty={{
          icon: 'card',
          title: table.isFiltered ? 'No plans match these filters' : 'No plans yet',
          description: table.isFiltered
            ? 'Try clearing the filters to see everything.'
            : 'Create a plan, then give it a price in each currency you sell in.',
          action: table.isFiltered ? (
            <Button type="button" onClick={() => table.clearFilters()}>
              Clear filters
            </Button>
          ) : (
            <Button type="button" onClick={() => setEditing({ new: true })}>
              Create your first plan
            </Button>
          ),
        }}
        toolbar={
          <FilterBar
            query={table.q}
            onQueryChange={table.setQuery}
            searchPlaceholder="Search slug, tier or key…"
            filters={[
              {
                key: 'audience',
                label: 'Audience',
                value: table.filters.audience ?? '',
                options: [
                  { value: 'family', label: 'Family' },
                  { value: 'school', label: 'School' },
                ],
              },
              {
                key: 'status',
                label: 'Status',
                value: table.filters.status ?? '',
                options: [
                  { value: 'active', label: 'Active' },
                  { value: 'archived', label: 'Archived' },
                ],
              },
            ]}
            onFilterChange={table.setFilter}
            onClear={table.clearFilters}
            isFiltered={table.isFiltered}
          />
        }
      />

      {editing && 'new' in editing && (
        <PlanEditor
          key="new"
          plan={undefined}
          onClose={() => setEditing(null)}
          onSaved={(slug) => {
            void invalidate();
            toast(`Plan ${slug} created`, { tone: 'success' });
            setEditing(null);
          }}
        />
      )}

      {editingPlan && (
        // `key` on the plan id so switching rows remounts the form rather than
        // carrying the previous plan's half-edited state into the next one.
        <PlanEditor
          key={editingPlan.id}
          plan={editingPlan}
          onClose={() => setEditing(null)}
          onSaved={(slug) => {
            void invalidate();
            toast(`Plan ${slug} saved`, { tone: 'success' });
            setEditing(null);
          }}
        />
      )}

      {pricePlan && (
        <Drawer
          open
          onOpenChange={(open) => !open && setPricesFor(null)}
          title={`Prices — ${pricePlan.slug}`}
          description="Each cell saves on its own. Leave a currency blank to not sell in it."
          width="lg"
        >
          <PriceGrid plan={pricePlan} onChanged={invalidate} />
        </Drawer>
      )}

      <ConfirmDialog
        open={pendingArchive != null}
        onOpenChange={(open) => !open && setPendingArchive(null)}
        title="Archive this plan"
        description={
          pendingArchive && (
            <>
              <span className="font-semibold">{pendingArchive.slug}</span> will stop being offered
              to new customers. Anyone already subscribed to it keeps their subscription and keeps
              being billed — archiving is a catalog change, not a cancellation. You can restore it
              at any time.
            </>
          )
        }
        confirmLabel="Archive plan"
        tone="primary"
        onConfirm={async () => {
          if (!pendingArchive) return;
          const slug = pendingArchive.slug;
          await setStatus.mutateAsync({ plan: pendingArchive, status: 'archived' });
          toast(`Plan ${slug} archived`, { tone: 'success' });
          setPendingArchive(null);
        }}
      />
    </div>
  );
}

// ─── The full plan editor ────────────────────────────────────────────────────

function PlanEditor({
  plan,
  onClose,
  onSaved,
}: {
  /** Undefined = create mode. */
  plan: AdminPlan | undefined;
  onClose: () => void;
  onSaved: (slug: string) => void;
}) {
  const [draft, setDraft] = useState<PlanDraft>(() =>
    plan ? draftFromPlan(plan) : emptyDraft('family'),
  );
  const [showErrors, setShowErrors] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const result = validate(draft);
  // Field errors stay hidden until the first submit attempt — a form that
  // turns red while you are still typing the first character is hostile.
  const fieldError = (f: PlanField) => (showErrors ? result.errors[f] : undefined);

  function set<K extends keyof PlanDraft>(key: K, value: PlanDraft[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
  }

  const save = useMutation({
    mutationFn: async () => {
      if (plan) {
        const patch = buildPatch(draft, plan);
        // Nothing changed — skip the round trip rather than writing an audit
        // row for a save that alters nothing.
        if (Object.keys(patch).length > 0) await updatePlan(plan.id, patch);
        return draft.slug.trim();
      }
      await createPlan(buildCreateInput(draft));
      return draft.slug.trim();
    },
    onSuccess: (slug) => onSaved(slug),
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Something went wrong.'),
  });

  const unchanged = plan ? isUnchanged(draft, plan) : false;
  const limits = visibleLimits(draft.audience, plan);

  return (
    <Drawer
      open
      onOpenChange={(open) => !open && onClose()}
      title={plan ? `Edit ${plan.slug}` : 'New plan'}
      description={
        plan
          ? 'Only the fields you change are sent.'
          : 'Create the plan, then set its prices per currency.'
      }
      badge={plan && <StatusBadge status={plan.status} dot />}
      width="xl"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="plan-editor" disabled={save.isPending || unchanged}>
            {save.isPending ? 'Saving…' : plan ? 'Save changes' : 'Create plan'}
          </Button>
        </>
      }
    >
      <form
        id="plan-editor"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          setShowErrors(true);
          if (result.ok) save.mutate();
        }}
      >
        {error && (
          <p role="alert" className="mb-4 rounded-lg bg-error-50 px-3 py-2 text-sm text-error-700">
            {error}
          </p>
        )}

        <Section title="Identity">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label="Slug"
              error={fieldError('slug')}
              hint="Lowercase, hyphenated. Used in URLs and by the marketing site."
            >
              <Input
                value={draft.slug}
                onChange={(e) => set('slug', e.target.value)}
                placeholder="family-plus"
                aria-invalid={fieldError('slug') != null}
              />
            </Field>

            <Field label="Audience">
              <Select
                value={draft.audience}
                onChange={(v) => set('audience', v as 'family' | 'school')}
                options={[
                  { value: 'family', label: 'Family' },
                  { value: 'school', label: 'School' },
                ]}
              />
              {/* Audience decides which limits mean anything, so changing it on
                  a live plan is not cosmetic. Not blocked — a mis-created plan
                  has to be fixable — but never silent. */}
              {plan && draft.audience !== plan.audience && (
                <Callout>
                  Changing the audience changes which limits apply and which pricing page shows this
                  plan. Existing subscriptions are unaffected.
                </Callout>
              )}
            </Field>

            <Field label="Tier" error={fieldError('tier')} hint="Free-text rank, e.g. basic, plus.">
              <Input
                value={draft.tier}
                onChange={(e) => set('tier', e.target.value)}
                placeholder="plus"
                aria-invalid={fieldError('tier') != null}
              />
            </Field>

            <Field label="Status">
              <Select
                value={draft.status}
                onChange={(v) => set('status', v as 'active' | 'archived')}
                options={[
                  { value: 'active', label: 'Active — sold to new customers' },
                  { value: 'archived', label: 'Archived — hidden from checkout' },
                ]}
              />
            </Field>
          </div>
        </Section>

        <Section
          title="Copy"
          hint="These are translation KEYS, not the text customers see. Edit the AR/EN values in Translations."
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Name key" error={fieldError('nameKey')}>
              <Input
                value={draft.nameKey}
                onChange={(e) => set('nameKey', e.target.value)}
                placeholder="plan.family_plus.name"
                className="font-mono text-sm"
                aria-invalid={fieldError('nameKey') != null}
              />
            </Field>
            <Field label="Tagline key (optional)" error={fieldError('taglineKey')}>
              <Input
                value={draft.taglineKey}
                onChange={(e) => set('taglineKey', e.target.value)}
                placeholder="plan.family_plus.tagline"
                className="font-mono text-sm"
                aria-invalid={fieldError('taglineKey') != null}
              />
            </Field>
          </div>

          <div className="mt-4">
            <p className="text-sm font-medium text-ink-700">Feature bullets</p>
            <p className="mt-0.5 text-xs text-admin-500">
              Shown on the pricing card, in order. Blank rows are dropped on save.
            </p>
            <FeatureRows
              rows={draft.features}
              onChange={(rows) => set('features', rows)}
              error={fieldError('features')}
            />
          </div>

          <label className="mt-4 flex items-start gap-2.5">
            <input
              type="checkbox"
              checked={draft.isHighlighted}
              onChange={(e) => set('isHighlighted', e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-admin-300 text-brand-500 focus:ring-brand-500/30"
            />
            <span className="text-sm text-ink-700">
              Mark as “Popular”
              <span className="mt-0.5 block text-xs font-normal text-admin-500">
                Adds the highlight badge on the pricing page. More than one highlighted plan makes
                the badge meaningless.
              </span>
            </span>
          </label>
        </Section>

        <Section
          title="Limits"
          hint="Unlimited is a real, stored value — it is not the same as leaving a number out."
        >
          <div className="grid gap-3">
            {limits.map((field) => (
              <LimitEditor
                key={field}
                field={field}
                draft={draft}
                onChange={setDraft}
                error={fieldError(field)}
                stale={
                  // A limit outside this audience's own set is a leftover from
                  // a mis-created plan. Surfaced rather than hidden, because a
                  // stale cap that nothing displays is still enforced.
                  !visibleLimits(draft.audience).includes(field)
                }
              />
            ))}
          </div>
        </Section>

        <Section
          title="Trial"
          hint="Per plan (owner ruling 2026-08-16). 0 days = no trial, which is the default."
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Trial length (days)" error={fieldError('trialDays')}>
              <Input
                value={draft.trialDays}
                onChange={(e) => set('trialDays', e.target.value)}
                inputMode="numeric"
                placeholder="0"
                className="max-w-[10rem]"
                aria-invalid={fieldError('trialDays') != null}
              />
            </Field>
          </div>

          <label className="mt-3 flex items-start gap-2.5">
            <input
              type="checkbox"
              checked={draft.trialRequiresCard}
              onChange={(e) => set('trialRequiresCard', e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-admin-300 text-brand-500 focus:ring-brand-500/30"
            />
            <span className="text-sm text-ink-700">
              Require a payment method to start the trial
              <span className="mt-0.5 block text-xs font-normal text-admin-500">
                Off means no payment step at all — at expiry the family drops to the free tier
                rather than being locked out.
              </span>
            </span>
          </label>
          {fieldError('trialRequiresCard') && (
            <p role="alert" className="mt-1 text-xs text-error-600">
              {fieldError('trialRequiresCard')}
            </p>
          )}
        </Section>

        {plan && plan.prices.length === 0 && draft.status === 'active' && (
          <Callout tone="warning">
            This plan is active but has no prices set, so it cannot be bought. Add at least one
            currency under Edit prices.
          </Callout>
        )}
      </form>
    </Drawer>
  );
}

// ─── Limits ──────────────────────────────────────────────────────────────────

function LimitEditor({
  field,
  draft,
  onChange,
  error,
  stale,
}: {
  field: LimitField;
  draft: PlanDraft;
  onChange: (fn: (d: PlanDraft) => PlanDraft) => void;
  error?: string;
  stale: boolean;
}) {
  const { mode, value } = limitOf(draft, field);
  const label = LIMIT_LABEL[field];

  return (
    <div className="rounded-xl border border-admin-200 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium text-ink-700">
          {label}
          {stale && (
            <span className="ms-2 rounded-md bg-warning-50 px-1.5 py-0.5 text-xs font-medium text-warning-700 ring-1 ring-inset ring-warning-500/20">
              Not used by {draft.audience} plans
            </span>
          )}
        </p>
        {/* Radios, not a checkbox or a magic empty string: the two states are
            mutually exclusive and both are real, storable values. */}
        <div className="flex gap-1">
          <ModeButton
            active={mode === 'unlimited'}
            onClick={() => onChange((d) => setLimitMode(d, field, 'unlimited'))}
          >
            Unlimited
          </ModeButton>
          <ModeButton
            active={mode === 'limited'}
            onClick={() => onChange((d) => setLimitMode(d, field, 'limited'))}
          >
            Limited to…
          </ModeButton>
        </div>
      </div>

      {mode === 'limited' ? (
        <div className="mt-2 flex items-center gap-2">
          <Input
            value={value}
            onChange={(e) => onChange((d) => ({ ...d, [field]: e.target.value }))}
            inputMode="numeric"
            placeholder="5"
            className="h-9 max-w-[8rem]"
            aria-label={`Maximum ${label.toLowerCase()}`}
            aria-invalid={error != null}
          />
          <span className="text-sm text-admin-500">{label.toLowerCase()} maximum</span>
        </div>
      ) : (
        <p className="mt-2 text-sm text-admin-500">
          No cap — stored as <code className="font-mono text-xs">null</code>, not 0.
        </p>
      )}

      {error && (
        <p role="alert" className="mt-1 text-xs text-error-600">
          {error}
        </p>
      )}
    </div>
  );
}

// ─── Features ────────────────────────────────────────────────────────────────

/**
 * Repeatable-row editor for the `features` jsonb array.
 *
 * A raw JSON textarea would have been fewer lines and is what the column
 * invites, but it makes a malformed array one stray comma away and gives the
 * operator no idea what the array is FOR. Rows it is.
 */
function FeatureRows({
  rows,
  onChange,
  error,
}: {
  rows: string[];
  onChange: (rows: string[]) => void;
  error?: string;
}) {
  const cleanedCount = cleanFeatures(rows).length;

  function update(index: number, value: string) {
    onChange(rows.map((r, i) => (i === index ? value : r)));
  }
  function remove(index: number) {
    const next = rows.filter((_, i) => i !== index);
    // Never leave the editor with zero rows — there would be nothing to type
    // into and the only affordance would be the Add button.
    onChange(next.length === 0 ? [''] : next);
  }
  function move(index: number, delta: number) {
    const target = index + delta;
    if (target < 0 || target >= rows.length) return;
    const next = [...rows];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved!);
    onChange(next);
  }

  return (
    <div className="mt-2">
      <ul className="space-y-2">
        {rows.map((row, i) => (
          // Index key, deliberately: a feature row is POSITIONAL — reordering
          // is the edit — and there is no stable id to key on. Keying by
          // content would remount the input on every keystroke and lose focus
          // mid-word.
          <li key={i} className="flex items-center gap-1.5">
            <Input
              value={row}
              onChange={(e) => update(i, e.target.value)}
              placeholder="Unlimited lessons"
              className="h-9"
              aria-label={`Feature ${i + 1}`}
            />
            <IconButton
              label={`Move feature ${i + 1} up`}
              icon="chevronDown"
              className="rotate-180"
              disabled={i === 0}
              onClick={() => move(i, -1)}
            />
            <IconButton
              label={`Move feature ${i + 1} down`}
              icon="chevronDown"
              disabled={i === rows.length - 1}
              onClick={() => move(i, 1)}
            />
            <IconButton
              label={`Remove feature ${i + 1}`}
              icon="trash"
              destructive
              onClick={() => remove(i)}
            />
          </li>
        ))}
      </ul>

      <div className="mt-2 flex items-center gap-3">
        <Button type="button" variant="ghost" size="sm" onClick={() => onChange([...rows, ''])}>
          <Icon name="plus" className="h-3.5 w-3.5" />
          Add feature
        </Button>
        <span className="text-xs text-admin-400">
          {cleanedCount} will be saved{rows.length !== cleanedCount && ' (blank rows dropped)'}
        </span>
      </div>

      {error && (
        <p role="alert" className="mt-1 text-xs text-error-600">
          {error}
        </p>
      )}
    </div>
  );
}

// ─── Prices ──────────────────────────────────────────────────────────────────

/**
 * The (currency, interval) grid. Unchanged in shape from the original page —
 * it works and the owner did not ask for it to move.
 *
 * 🔴 The two fixes it did get: amounts now round-trip through lib/money.ts
 * (`parseMinorAmount` knows KWD/BHD/OMR are 3-decimal; the old inline
 * `Math.round(parseFloat(v) * 10 ** digits)` was a second copy of that rule),
 * and a cell only offers Save when its value actually differs from what is
 * stored — the old one let you re-save an unchanged price, writing an audit
 * row for a no-op.
 */
function PriceGrid({ plan, onChanged }: { plan: AdminPlan; onChanged: () => void }) {
  return (
    <div className="space-y-4">
      {CURRENCIES.map((currency) => (
        <div key={currency}>
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-admin-500">
            {currency}
            <span className="ms-2 font-normal normal-case tracking-normal text-admin-400">
              {currencyDigits(currency)} decimals
            </span>
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            {INTERVALS.map((interval) => {
              const existing = plan.prices.find(
                (p) => p.currency === currency && p.interval === interval,
              );
              return (
                <PriceCell
                  key={`${currency}-${interval}`}
                  planId={plan.id}
                  currency={currency}
                  interval={interval}
                  // Format FROM minor units rather than trusting displayAmount,
                  // so the box always shows the currency's real precision.
                  current={
                    existing
                      ? formatMinorAmount(existing.amountMinor, currency).replace(/,/g, '')
                      : ''
                  }
                  onChanged={onChanged}
                />
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function PriceCell({
  planId,
  currency,
  interval,
  current,
  onChanged,
}: {
  planId: string;
  currency: string;
  interval: 'month' | 'year';
  current: string;
  onChanged: () => void;
}) {
  const [value, setValue] = useState(current);
  const [error, setError] = useState<string | null>(null);

  const minor = parseMinorAmount(value, currency);
  const dirty = value.trim() !== current.trim();
  // A blank box is not an error, it is "we do not sell this combination" —
  // but there is no DELETE endpoint for a price, so it simply cannot be saved.
  const invalid = value.trim() !== '' && minor === null;

  const save = useMutation({
    mutationFn: () => {
      if (minor === null) throw new Error('Invalid amount');
      return upsertPrice(planId, {
        currency: currency as never,
        interval,
        displayAmount: value.trim(),
        amountMinor: minor,
      });
    },
    onSuccess: () => {
      setError(null);
      onChanged();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not save.'),
  });

  return (
    <div className="rounded-xl bg-admin-50 px-3 py-2">
      <div className="flex items-center gap-2">
        <span className="w-10 shrink-0 text-xs font-medium text-admin-500">
          {interval === 'month' ? 'mo' : 'yr'}
        </span>
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          inputMode="decimal"
          placeholder="—"
          className="h-8"
          aria-label={`${currency} per ${interval}`}
          aria-invalid={invalid}
        />
        <Button
          size="sm"
          onClick={() => save.mutate()}
          disabled={save.isPending || !dirty || invalid || value.trim() === ''}
        >
          Save
        </Button>
      </div>
      {invalid && (
        <p role="alert" className="mt-1 text-xs text-error-600">
          Max {currencyDigits(currency)} decimal places for {currency}.
        </p>
      )}
      {error && (
        <p role="alert" className="mt-1 text-xs text-error-600">
          {error}
        </p>
      )}
    </div>
  );
}

// ─── Small shared bits ───────────────────────────────────────────────────────

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="border-b border-admin-100 py-4 first:pt-0 last:border-0">
      <h3 className="text-[11px] font-semibold uppercase tracking-wider text-admin-500">{title}</h3>
      {hint && <p className="mb-3 mt-0.5 text-xs text-admin-500">{hint}</p>}
      <div className={hint ? '' : 'mt-3'}>{children}</div>
    </section>
  );
}

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block text-sm font-medium text-ink-700">
      {label}
      {children}
      {hint && !error && (
        <span className="mt-1 block text-xs font-normal text-admin-500">{hint}</span>
      )}
      {error && (
        <span role="alert" className="mt-1 block text-xs font-normal text-error-600">
          {error}
        </span>
      )}
    </label>
  );
}

function Select({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (value: string) => void;
  options: ReadonlyArray<{ value: string; label: string }>;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 h-11 w-full rounded-xl border border-ink-500/20 bg-white px-3 text-ink-900"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function ModeButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-lg px-2.5 py-1 text-xs font-medium transition-colors ${
        active
          ? 'bg-brand-500 text-white'
          : 'border border-admin-200 bg-white text-admin-700 hover:bg-admin-50'
      }`}
    >
      {children}
    </button>
  );
}

function IconButton({
  label,
  icon,
  onClick,
  disabled,
  destructive,
  className,
}: {
  label: string;
  icon: 'chevronDown' | 'trash';
  onClick: () => void;
  disabled?: boolean;
  destructive?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg transition-colors disabled:opacity-30 ${
        destructive
          ? 'text-admin-400 hover:bg-error-50 hover:text-error-600'
          : 'text-admin-400 hover:bg-admin-100 hover:text-admin-700'
      }`}
    >
      <Icon name={icon} className={`h-4 w-4 ${className ?? ''}`} />
    </button>
  );
}

function Callout({
  children,
  tone = 'info',
}: {
  children: React.ReactNode;
  tone?: 'info' | 'warning';
}) {
  return (
    <p
      className={`mt-2 flex items-start gap-1.5 rounded-lg px-3 py-2 text-xs leading-relaxed ${
        tone === 'warning' ? 'bg-warning-50 text-warning-700' : 'bg-admin-50 text-admin-600'
      }`}
    >
      <Icon name="alert" className="mt-px h-3.5 w-3.5 shrink-0" />
      <span>{children}</span>
    </p>
  );
}
